import jsonStringfy from 'fast-json-stable-stringify';
import { EvaluationErrorKind, ReadyReason } from '../api/types';
import ExposureEvents from '../exposureEvents';
import getLogger from '../logger';
import Storage from '../storage';
import Subscriptions, { Broadcast } from '../subscriptions';
import cloneObject from '../util/clone-object';
import Anonymous from './Anonymous';
import Ready, { READY_CACHE, READY_FETCH } from './Ready';
import Refresh from './Refresh';
import { flagStateToFlagUpdate, getReadyMessageForStatusCode, getReasonForStatusCode, hasFlagValueChanged, validateConstructorArgs, validateDefaultValue, validateFlag } from './util';
export const STORAGE_PURGE_TIMEOUT = 10000;
export default class Client {
  constructor(apiKey, analyticsWebClient, originalFeatureFlagUser, options) {
    // Should be done ASAP to prevent calls to console before usage
    getLogger(options === null || options === void 0 ? void 0 : options.loggerOptions);
    validateConstructorArgs(apiKey, analyticsWebClient, originalFeatureFlagUser, options);
    const featureFlagUser = cloneObject(originalFeatureFlagUser);
    const user = Anonymous.processAnonymousUser(featureFlagUser);
    this.featureFlagUpdateBroadcastHandler = this.featureFlagUpdateBroadcastHandler.bind(this);
    this.featureFlagUpdateHttpHandler = this.featureFlagUpdateHttpHandler.bind(this);
    this.featureFlagUpdateFailedHandler = this.featureFlagUpdateFailedHandler.bind(this);
    this.readyResolver = new Ready({
      readyTimeoutInMilliseconds: options.readyTimeoutInMilliseconds
    });
    this.exposureEvents = new ExposureEvents(analyticsWebClient);
    this.storage = new Storage(options.environment, options.productKey, apiKey, user);
    this.broadcast = new Broadcast(apiKey, user, this.featureFlagUpdateBroadcastHandler);
    this.restoreFlagStateFromStorage();
    this.userData = user;
    this.refresh = new Refresh(apiKey, options.environment, this.userData, this.featureFlagUpdateHttpHandler, {
      interval: options.pollingInterval
    }, this.dataVersion, this.lastUpdatedTimestamp, this.featureFlagUpdateFailedHandler, {}, options.useGatewayURL, options.perimeter);
    this.refresh.start();
    this.subscriptions = new Subscriptions();

    // Dont run on startup to prevent slowdown
    setTimeout(() => this.storage.purgeStaleFlagState(), STORAGE_PURGE_TIMEOUT);
  }
  ready() {
    return this.readyResolver.getPromise();
  }
  getFlagValue(flagKey, defaultValue, options) {
    const validatedFlag = this.getFlagDetails(flagKey, defaultValue, options);
    return validatedFlag.value;
  }
  getFlagDetails(flagKey, defaultValue, options) {
    validateDefaultValue(defaultValue);
    const flag = this.flags[flagKey];
    const validatedFlag = validateFlag(flag, defaultValue, this.refresh.getStatus(), options);
    const shouldSendExposureEvent = (options === null || options === void 0 ? void 0 : options.shouldSendExposureEvent) !== false && !this.shouldIgnoreExposureForMissingFlag(flagKey, validatedFlag);
    if (shouldSendExposureEvent) {
      // If the shouldSendExposureEvent is not defined at all, we may fire an automatic event.
      const isExplicitlyRequested = (options === null || options === void 0 ? void 0 : options.shouldSendExposureEvent) === true;
      this.exposureEvents.sendExposureEvent(flagKey, validatedFlag, isExplicitlyRequested, options === null || options === void 0 ? void 0 : options.exposureData);
    }
    return validatedFlag;
  }

  // Skip sending automatic exposure for platform flags as they are often released to a single product,
  // and registering them for all products that consume platform components is costly.
  shouldIgnoreExposureForMissingFlag(flagKey, validatedFlag) {
    var _validatedFlag$evalua;
    if (((_validatedFlag$evalua = validatedFlag.evaluationDetail) === null || _validatedFlag$evalua === void 0 ? void 0 : _validatedFlag$evalua.errorKind) !== EvaluationErrorKind.FLAG_NOT_FOUND) {
      return false;
    }
    if (!flagKey.startsWith('platform.')) {
      return false;
    }
    return true;
  }
  getFlags() {
    return cloneObject(this.flags);
  }
  on(flagKey, defaultValue, callback, options) {
    validateDefaultValue(defaultValue);
    return this.subscriptions.on(flagKey, defaultValue, callback, this.getFlagValue.bind(this), options);
  }
  onAnyFlagUpdated(callback) {
    return this.subscriptions.onAnyFlagUpdated(callback);
  }
  updateFeatureFlagUser(originalFeatureFlagUser) {
    if (!originalFeatureFlagUser) {
      throw new Error('featureFlagUser is missing');
    }
    const featureFlagUser = cloneObject(originalFeatureFlagUser);
    const user = Anonymous.processAnonymousUser(featureFlagUser);
    if (jsonStringfy(this.userData) === jsonStringfy(user)) {
      return this.ready();
    }
    this.readyResolver.reset();
    this.userData = user;
    this.storage.updateUserContext(user);
    const storedData = this.storage.getFlagsState();
    if (storedData) {
      const changedFlagKeys = this.updateInMemoryFlags(flagStateToFlagUpdate(this.flags, storedData));
      this.sendSubscriptionUpdates(changedFlagKeys);
      this.readyResolver.triggerReady(READY_CACHE);
    }
    this.lastUpdatedTimestamp = (storedData === null || storedData === void 0 ? void 0 : storedData.timestamp) || 0;
    this.dataVersion = storedData === null || storedData === void 0 ? void 0 : storedData.version;
    this.refresh.stop();
    this.refresh.updateUserContext(user, this.dataVersion, this.lastUpdatedTimestamp);
    this.refresh.start();
    this.broadcast.updateUserContext(user);
    return this.ready();
  }
  addFlag(flagKey, flag) {
    this.flags[flagKey] = flag;
  }

  // Convenience API, not official
  destroy() {
    this.refresh.stop();
    this.subscriptions.stop();
    this.broadcast.stop();
    this.readyResolver.triggerReady({
      reason: ReadyReason.CLIENT_ERROR,
      message: 'Client got destroyed.'
    });
  }
  setAutomaticExposuresEnabled(enabled, predicate) {
    this.exposureEvents.setAutomaticExposuresEnabled(enabled, predicate);
  }
  restoreFlagStateFromStorage() {
    const storedData = this.storage.getFlagsState();
    this.flags = (storedData === null || storedData === void 0 ? void 0 : storedData.flags) || {};
    this.lastUpdatedTimestamp = (storedData === null || storedData === void 0 ? void 0 : storedData.timestamp) || 0;
    this.dataVersion = storedData === null || storedData === void 0 ? void 0 : storedData.version;
    if (storedData !== null && storedData !== undefined) {
      this.readyResolver.triggerReady(READY_CACHE);
    } else {
      // Attempt to recover old flags
      const oldFlagState = this.storage.getFlagsFromOldState();
      if (oldFlagState) {
        // Do not update lastUpdatedTimestamp or dataVersion
        // to force refresh to run immediately without the cache
        this.flags = oldFlagState.flags;
        this.readyResolver.triggerReady(READY_CACHE);
      }
    }
  }
  getCurrentFeatureFlagState() {
    return {
      timestamp: this.lastUpdatedTimestamp,
      version: this.dataVersion,
      flags: this.flags
    };
  }
  featureFlagUpdateFailedHandler(statusCode, rawBody) {
    this.readyResolver.triggerReady({
      reason: getReasonForStatusCode(statusCode),
      message: getReadyMessageForStatusCode(statusCode),
      serverResponse: rawBody
    });
  }
  featureFlagUpdateHttpHandler(update) {
    if (this.dataVersion) {
      this.processFeatureFlagUpdate(update);
    } else {
      // If dataVersion is undefined, then the request had no versionData.
      // This would mean that the API cannot tell us what flags to delete,
      // and has just given us every flag available at its latest state.
      // We should replace all flags instead of merging the flags.
      this.processFeatureFlagUpdate(flagStateToFlagUpdate(this.flags, update));
    }
    // broadcast the flag state to other tabs
    this.broadcast.sendFeatureFlagState(this.getCurrentFeatureFlagState());
  }
  featureFlagUpdateBroadcastHandler(featureFlagState) {
    // return if broadcasted ff state is stale
    if (featureFlagState.timestamp < this.lastUpdatedTimestamp) {
      return;
    }
    this.processFeatureFlagUpdate(flagStateToFlagUpdate(this.flags, featureFlagState));
  }
  processFeatureFlagUpdate(update) {
    this.lastUpdatedTimestamp = Date.now();
    this.dataVersion = update.versionData || this.dataVersion;
    const changedFlagKeys = this.updateInMemoryFlags(update);
    this.sendSubscriptionUpdates(changedFlagKeys);
    this.refresh.setVersionAndTimestamp(this.dataVersion, this.lastUpdatedTimestamp);
    this.storage.setFlagsState(this.getCurrentFeatureFlagState());
    this.readyResolver.triggerReady(READY_FETCH);
  }
  updateInMemoryFlags(update) {
    var _update$deletedFlags;
    const changedFlags = [];
    (_update$deletedFlags = update.deletedFlags) === null || _update$deletedFlags === void 0 ? void 0 : _update$deletedFlags.forEach(flagKey => {
      if (flagKey in this.flags) {
        delete this.flags[flagKey];
        changedFlags.push(flagKey);
      }
    });
    Object.keys(update.flags).forEach(flagKey => {
      if (hasFlagValueChanged(this.flags[flagKey], update.flags[flagKey])) {
        changedFlags.push(flagKey);
      }
      this.flags[flagKey] = update.flags[flagKey];
    });
    return changedFlags;
  }
  sendSubscriptionUpdates(changedFlagKeys) {
    if (changedFlagKeys.length > 0) {
      this.subscriptions.anyFlagUpdated();
      changedFlagKeys.forEach(flagKey => this.subscriptions.flagValueUpdated(flagKey));
    }
  }
}